var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect } from 'react';
import { MdClose, MdCompareArrows, MdFunctions } from 'react-icons/md';
import { useLanguage } from '@base/LanguageProvider';
import FSButton from '@components/FSButton';
import FSToggle from '@components/FSToggle';
import { useUpdateUserSettingsActions } from '@data/hooks/currentUser';
import { useActiveYears } from '@data/hooks/timePoints';
import { useActiveVersions } from '@data/hooks/versions';
import { equalComparison, generateComparisonOptions, getComparisonLabel } from '@data/timeline/comparisons';
import CheckboxMenu from '@shared/input/CheckboxMenu';
import DateIntervalSlider from '@shared/input/DateIntervalSlider';
import { ComparisonAndResetContainer, IntervalToolbarContainer, PeriodsContainer, PeriodsDivider, } from '@styled/IntervalToolbar';
import { modelDiffVersionsFormatMeta, Period } from '@util/enums';
import { parseInterval } from '@util/helpers';
import ModelColumnEditor from './ModelColumnEditor';
import { useAuth } from '../../providers/AuthProvider';
import { useTagContext } from '../../data/tag/tagContext';
function IntervalToolbar(props) {
    var _a;
    var _b = props.showColumnManager, showColumnManager = _b === void 0 ? true : _b;
    var userSettings = useAuth().settings;
    var intervalStart = userSettings.model_table_interval_start;
    var intervalEnd = userSettings.model_table_interval_end;
    var periods = userSettings.model_table_periods;
    var showSum = userSettings.model_table_show_sum;
    var _c = useUpdateUserSettingsActions(), setModelTableIntervalStart = _c.setModelTableIntervalStart, setModelTableIntervalEnd = _c.setModelTableIntervalEnd, addModelTableYear = _c.addModelTableYear, removeModelTableYear = _c.removeModelTableYear, setModelTablePeriod = _c.setModelTablePeriod, toggleModelTableShowSum = _c.toggleModelTableShowSum, toggleModelTimelineVersionByYear = _c.toggleModelTimelineVersionByYear;
    var years = useActiveYears();
    var activeYears = (_a = props.activeYears) !== null && _a !== void 0 ? _a : years;
    var _d = parseInterval(intervalStart, intervalEnd, activeYears), startYear = _d.startYear, endYear = _d.endYear, startMonth = _d.startMonth, endMonth = _d.endMonth;
    var actions = {
        setStart: function (startInterval) { return setModelTableIntervalStart(startInterval); },
        setEnd: function (endInterval) { return setModelTableIntervalEnd(endInterval); },
        addYear: function (year) { return addModelTableYear(year); },
        removeYear: function (year) { return removeModelTableYear(year); },
        setPeriod: function (period) { return setModelTablePeriod(period); },
        toggleSum: function () { return toggleModelTableShowSum(); },
    };
    function intervalChangeHandler(startInterval, endInterval) {
        if (startInterval !== intervalStart)
            actions.setStart(startInterval);
        if (endInterval !== intervalEnd)
            actions.setEnd(endInterval);
        var _a = parseInterval(startInterval, endInterval, activeYears), startYear = _a.startYear, endYear = _a.endYear;
        activeYears.forEach(function (y) {
            if (y > endYear || y < startYear) {
                actions.removeYear(y);
            }
            else {
                actions.addYear(y);
            }
        });
    }
    useEffect(function () {
        if (activeYears.length === 0) {
            return;
        }
        //Handles input setup year deactivation
        if (endYear > activeYears[activeYears.length - 1] || !activeYears.includes(endYear)) {
            var updatedEnd = "".concat("".concat(endMonth).length > 1 ? endMonth : "0".concat(endMonth), "/").concat(activeYears[activeYears.length - 1]);
            var updatedStartYear = activeYears[activeYears.length - 1] < startYear
                ? activeYears[activeYears.length - 1] - (endYear - startYear)
                : startYear;
            var updatedStart = "".concat(startMonth, "/").concat(updatedStartYear);
            intervalChangeHandler(updatedStart, updatedEnd);
        }
        //Handles input setup year activation
        if (startYear < activeYears[0] || !activeYears.includes(startYear)) {
            var updatedStart = "".concat("".concat(startMonth).length > 1 ? startMonth : "0".concat(startMonth), "/").concat(activeYears[0]);
            var updatedEndYear = activeYears[0] > endYear ? activeYears[0] + (endYear - startYear) : endYear;
            var updatedEnd = "".concat(endMonth, "/").concat(updatedEndYear);
            intervalChangeHandler(updatedStart, updatedEnd);
        }
    }, [startYear, endYear, startMonth, endMonth, activeYears]);
    var periodButtons = Object.values(Period).map(function (pe) {
        var titleLetter = pe
            .split(' ')
            .map(function (w) { return w[0].toUpperCase(); })
            .join('');
        var isActive = !!periods.find(function (p) { return p === pe; });
        return React.createElement(FSToggle, { key: pe, checked: isActive, title: titleLetter, onChange: function () { return actions.setPeriod(pe); } });
    });
    var startInterval = "".concat(startMonth, "/").concat(startYear);
    var endInterval = "".concat(endMonth, "/").concat(endYear);
    return (React.createElement(IntervalToolbarContainer, null,
        React.createElement(PeriodsContainer, null,
            showColumnManager && React.createElement(ModelColumnEditor, null),
            periodButtons,
            React.createElement(PeriodsDivider, null),
            React.createElement(FSToggle, { icon: MdFunctions, checked: showSum, onChange: actions.toggleSum })),
        React.createElement(DateIntervalSlider, { start: startInterval, end: endInterval, range: activeYears, steps: 1, onIntervalChange: intervalChangeHandler, yearVersions: userSettings.model_table_timeline_versions_by_year, yearVersionToggle: toggleModelTimelineVersionByYear, activeVersions: props.activeVersions, activeYears: props.activeYears }),
        React.createElement(ComparisonAndReset, { startYear: startYear, endYear: endYear, activeVersions: props.activeVersions })));
}
function ComparisonAndReset(props) {
    var _a;
    var startYear = props.startYear, endYear = props.endYear;
    var _b = useUpdateUserSettingsActions(), toggleModelTimelineComparison = _b.toggleModelTimelineComparison, toggleModelTimelineComparisonReverse = _b.toggleModelTimelineComparisonReverse, toggleModelTimelineDiffFormat = _b.toggleModelTimelineDiffFormat, resetModelTimeline = _b.resetModelTimeline;
    var userSettings = useAuth().settings;
    var versions = useActiveVersions();
    var tagData = useTagContext().tagData;
    var activeVersions = (_a = props.activeVersions) !== null && _a !== void 0 ? _a : versions;
    var L = useLanguage().L;
    var versionsById = activeVersions.reduce(function (map, version) {
        map[version.id] = version;
        return map;
    }, {});
    var comparisons = generateComparisonOptions(startYear, endYear, userSettings.model_table_timeline_versions_by_year, versionsById, tagData.internalTagsMap.years);
    var isComparisonDisabled = comparisons.length === 0;
    var isResetDisabled = Object.keys(userSettings.model_table_timeline_versions_by_year).length === 0;
    var menuItems = comparisons.map(function (c) {
        var found = userSettings.model_table_timeline_comparisons.find(function (cc) { return equalComparison(cc, c); });
        return {
            label: found ? getComparisonLabel(found) : getComparisonLabel(c),
            checked: !!found,
            onToggle: function (newChecked) { return toggleModelTimelineComparison(c, newChecked); },
            afterButton: !!found
                ? { icon: MdCompareArrows, action: function () { return toggleModelTimelineComparisonReverse(found); } }
                : undefined,
        };
    });
    menuItems.push({ spacer: true });
    menuItems.push.apply(menuItems, __spreadArray([], __read(modelDiffVersionsFormatMeta.map(function (diffFormat) {
        var checked = userSettings.model_table_timeline_diffformat.includes(diffFormat.value);
        return {
            label: diffFormat.label,
            checked: checked,
            onToggle: function () { return toggleModelTimelineDiffFormat(diffFormat.value); },
        };
    })), false));
    return (React.createElement(ComparisonAndResetContainer, null,
        React.createElement(CheckboxMenu, { title: L('Comparison'), items: menuItems, disabled: isComparisonDisabled }),
        React.createElement(FSButton, { "$color": "error", "$type": "tertiary", icon: MdClose, onClick: resetModelTimeline, disabled: isResetDisabled })));
}
export default IntervalToolbar;
